import { API } from "@swivl-mono/api-helper";
import { VarsHelper, filterClasses } from "@swivl-mono/helpers";
import { UserInterface, UserVarKey } from "@swivl-mono/types";
import { Spinner, useOutsideClick } from "@swivl-mono/ui";
import { memo, useEffect } from "react";
import { useParams } from "react-router-dom";

import useAutoFocus from "../../../../Hooks/useAutoFocus";
import useKeyPressAction from "../../../../Hooks/useKeyPressAction";
import { Member } from "../../../../Models/Member/Member.Current";
import { QuickReplyItem, QuickReplyModel } from "../../../../Models/QuickReply/QuickReply.Model";
import { UsersModel } from "../../../../Models/Users/Users.Model";

interface QuickRepliesDropdownProps {
  isLoading: boolean;
  isQuickRepliesDropdownOpen?: boolean;
  handleSetText: (text: string) => void;
  handleToggleQuickRepliesDropdown: (isOpen: boolean) => void;
  quickReplies?: QuickReplyItem[];
  user: UserInterface;
}

const BaseQuickRepliesDropdown = ({
  isLoading,
  quickReplies,
  isQuickRepliesDropdownOpen,
  handleToggleQuickRepliesDropdown,
  handleSetText,
  user
}: QuickRepliesDropdownProps) => {
  const menuRef = useOutsideClick(() => handleToggleQuickRepliesDropdown(false));
  const { greeting } = QuickReplyModel.useState((s) => s);
  const params = useParams();
  const { userId } = params;
  const { users } = UsersModel.useState((s) => s);
  const currentUser = userId && users?.[userId];
  useAutoFocus(menuRef, [isQuickRepliesDropdownOpen]);

  useKeyPressAction({
    callback: () => handleToggleQuickRepliesDropdown(false),
    key: "Escape",
    targetEl: menuRef.current
  });

  const handleSetQuickReply = async (QuickReply: QuickReplyItem) => {
    if (currentUser && user) {
      await API.UserVars.saveUserVar(user.orgId, user.id, UserVarKey.LastAgentName, Member.displayName);
      const message = await VarsHelper.replaceVariablesInString(currentUser, QuickReply.message);
      handleSetText(message);
      handleToggleQuickRepliesDropdown(false);
    } else {
      console.warn("User not found");
    }
  };

  useEffect(() => {
    if (greeting) {
      handleSetQuickReply(greeting);
    }
  }, [greeting]);

  if (isLoading) {
    return (
      <div className="flex h-12 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div
      aria-expanded={isQuickRepliesDropdownOpen}
      className={filterClasses(
        "min-h-0 w-full overflow-y-auto rounded-b-md shadow-lg transition-all duration-300 ease-in-out",
        isQuickRepliesDropdownOpen ? "visible mt-3.5 max-h-32 py-1" : "invisible mt-0 max-h-0 py-0"
      )}
      ref={menuRef}
    >
      <div className={filterClasses("flex flex-col gap-2 transition-all duration-1000")}>
        {quickReplies?.map((quickReply) => (
          <div className="flex w-full flex-col px-2 transition-colors duration-200 hover:bg-grey-200" key={quickReply.id}>
            <button className="text-left" onClick={() => handleSetQuickReply(quickReply)}>
              <span className="py-2 text-grey-600">{quickReply.name}</span>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const QuickRepliesDropdown = memo(BaseQuickRepliesDropdown);
